import React, { useCallback, useEffect, useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useLocation, matchPath } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import _lodash from 'lodash';

import useUserHasFullAuth from 'utils/hooks/auth/useUserHasFullAuth';
import useUserRoles from 'utils/hooks/auth/useUserRoles';
import useRedirect from 'utils/hooks/useRedirect';
import useCurrentLanguage from 'utils/hooks/useCurrentLanguage';
import useMedia from 'utils/hooks/useMedia';

import { toggleNavBurger } from 'store/entities/ui';
import { resetSubmissionsState } from 'store/entities/submissions';

import { Link } from 'react-router-dom';
import { IconButton } from 'components/UIComponents/Button';
import { mdiPlus } from '@mdi/js';

import NavMobile from './NavMobile';
import Navbar from 'react-bulma-components/lib/components/navbar';

import statLogo from 'assets/img/statistikaamet-logo.svg';
import statLogoEn from 'assets/img/statistikaamet-logo-en.svg';
import './style.scss';

const NavSecondary = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const isAuthenticated = useUserHasFullAuth();
  const redirect = useRedirect();
  const dispatch = useDispatch();
  const language = useCurrentLanguage();
  const { isMainUser, isReporter } = useUserRoles();

  const { institutions, institutionsLoading } = useSelector(state => state.institutions);
  const { navBurgerOpen } = useSelector(state => state.ui);

  const isMobile = useMedia(
    ['(max-width: 1023px)', '(min-width: 1px)'],
    [true, false],
    false
  );

  const paths = useMemo(
    () => [
      ...(isAuthenticated && !institutionsLoading && (isMainUser || isReporter)
        ? [
            {
              label: t('datagate.external.submissions.title'),
              path: [''],
              exact: true,
            },
          ]
        : []),
      ...institutions.map(({ name, id }) => ({
        label: name,
        path: [`/${id}/submissions`],
      })),
    ],
    [isAuthenticated, institutions, institutionsLoading, isMainUser, isReporter, t]
  );

  const buildNav = useCallback(() => {
    const navContainer = document.querySelector('.navbar__secondary');
    const showMoreBtn = navContainer.querySelector('.dropdown');

    let stopWidth = showMoreBtn ? showMoreBtn.offsetWidth : 0;

    if (showMoreBtn)
      navContainer.querySelector('.navbar-start').removeChild(showMoreBtn);

    document
      .querySelectorAll('.dynamic')
      .forEach(e => e.parentElement.removeChild(e));

    if (document.documentElement.clientWidth <= 1023) return;

    let hiddenItems = [];
    const primaryWidth = navContainer.querySelector('.navbar-menu').offsetWidth;

    navContainer.querySelector('.navbar-start').insertAdjacentHTML(
      'beforeend',
      ReactDOMServer.renderToStaticMarkup(
        paths.map(path => (
          <Navbar.Item className="dynamic">
            <Navbar.Link arrowless>{path.label}</Navbar.Link>
          </Navbar.Item>
        ))
      )
    );

    const navItems = navContainer.querySelectorAll('.navbar-item');

    Array.from(navItems)
      .slice(1)
      .forEach((item, i) => {
        if (primaryWidth >= stopWidth + item.offsetWidth) {
          item.classList.remove('hidden');
          stopWidth += item.offsetWidth;
        } else {
          item.classList.add('hidden');
          hiddenItems.push(i);
        }
        const { path, defaultPath } = paths[i];

        const isActivePath = !!matchPath(
          window.location.pathname.slice(process.env.PUBLIC_URL.length + 3),
          {
            path,
            exact: true,
          }
        );

        if (isActivePath) {
          item.classList.add('is-active');
        } else {
          item.classList.remove('is-active');
        }

        item.onclick = function() {
          redirect(defaultPath ?? path[0]);
        };
      });
    const overflowCount = hiddenItems.length;

    if (overflowCount > 0) {
      document
        .querySelector('.navbar__secondary')
        .querySelector('.navbar-start')
        .insertAdjacentHTML(
          'beforeend',
          ReactDOMServer.renderToStaticMarkup(
            <div
              className="dropdown is-hoverable"
              aria-label="Institution dropdown"
              aria-haspopup="menu"
            >
              <IconButton path={mdiPlus} type="button" />
              <div className="dropdown-menu" role="menu">
                <div className="dropdown-content">
                  {paths.slice(paths.length - overflowCount).map(
                    (path, i) =>
                      hiddenItems.includes(paths.indexOf(path)) && (
                        <div
                          key={'dropdown' + i}
                          className="dropdown-item"
                          onClick={() => {
                            dispatch(resetSubmissionsState());
                            redirect(path.defaultPath ?? path.path[0]);
                          }}
                        >
                          {path.label}
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          )
        );

      document
        .querySelector('.navbar__secondary')
        .querySelectorAll('.dropdown-item')
        .forEach((item, i) => {
          const path = paths.slice(paths.length - overflowCount)[i];
          item.onclick = function() {
            dispatch(resetSubmissionsState());
            redirect(path.defaultPath ?? path.path[0]);
          };
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    window.location.pathname,
    pathname,
    paths,
    isMobile,
    language,
    isAuthenticated,
  ]);

  useEffect(() => {
    const buildNavDebounced = _lodash.debounce(buildNav, 100);
    window.addEventListener('resize', buildNavDebounced);

    return () => window.removeEventListener('resize', buildNavDebounced);
  }, [buildNav]);

  useEffect(() => {
    buildNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    window.location.pathname,
    pathname,
    paths,
    isMobile,
    language,
    isAuthenticated,
  ]);

  return (
    <Navbar
      className="navbar__secondary dotted"
      aria-label="Secondary navigation"
      active={navBurgerOpen}
    >
      <Navbar.Brand>
        <Link to="/" className="navbar-item">
          <img
            src={language === 'en' ? statLogoEn : statLogo}
            alt="Statistikaameti logo"
            className="navbar-logo"
            width="140"
          />
        </Link>
        {isAuthenticated && (
          <Navbar.Burger
            onClick={() => dispatch(toggleNavBurger(!navBurgerOpen))}
          />
        )}
      </Navbar.Brand>
      <Navbar.Menu className={isMobile ? 'navbar-menu--mobile' : ''}>
        <Navbar.Container>
          {isMobile && isAuthenticated && <NavMobile paths={paths} />}
        </Navbar.Container>
      </Navbar.Menu>
    </Navbar>
  );
};

export default NavSecondary;
